import { PropsWithChildren, useEffect, ElementType, FC, memo } from 'react';
import classNames from 'classnames';

import ContactWithManagerButton from 'application/components/ContactWithManagerButton';
import LogoutSmallButton from 'application/components/LogoutSmallButton';
import Logo from 'application/components/Logo';
import { useAppSelector } from 'application/providers/StoreProvider';
import { selectShowContactWithManagerButton, selectIsAuthorized } from 'application/store';
import WebPlayerWidget from 'application/components/widgets/WebPlayer';
import { useWebPlayerContext } from 'application/providers/WebPlayerProvider';
import { useModalContext } from 'application/providers/ModalProvider';

import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import styles from './index.module.scss';

const DEFAULT_TITLE = 'Звук Бизнес';

export type CleanMainProps = PropsWithChildren<{
  containerClassName?: string;
  contentClassName?: string;
  title?: string;
  hideLogoutButton?: boolean;
  contentContainerAs?: ElementType;
}>;

const CleanMain: FC<CleanMainProps> = ({
  children,
  title,
  contentContainerAs: Renderer = 'div',
  contentClassName,
  containerClassName,
  hideLogoutButton = false,
}) => {
  const isAuthorized = useAppSelector(selectIsAuthorized);
  const { isDesktop, isMobile } = useMedia();
  const showContactWithManagerButton = useAppSelector(selectShowContactWithManagerButton);

  const { isWidgetActive } = useWebPlayerContext();
  const { currentModals } = useModalContext();
  const hasOpenModal = currentModals.length > 0;

  useEffect(() => {
    document.title = title || DEFAULT_TITLE;
  }, [title]);

  return (
    <main className={styles.CleanMain}>
      <header className={classNames(styles.CleanMainHeader, hasOpenModal && styles.CleanMainHeaderHidden)}>
        {isMobile && showContactWithManagerButton && (
          <ContactWithManagerButton className={styles.CleanMainContactButton} />
        )}

        <Logo />

        {isMobile && isAuthorized && <LogoutSmallButton className={styles.CleanMainLogoutButton} />}
      </header>

      <section className={classNames(styles.CleanMainSection, containerClassName)}>
        <Renderer className={classNames(styles.CleanMainSectionContent, contentClassName)}>{children}</Renderer>
      </section>

      {isDesktop && showContactWithManagerButton && (
        <ContactWithManagerButton className={styles.CleanMainContactButton} />
      )}

      {isDesktop && isAuthorized && !hideLogoutButton && <LogoutSmallButton className={styles.CleanMainLogoutButton} />}

      {isWidgetActive && <WebPlayerWidget />}
    </main>
  );
};

export default memo(CleanMain);
